/**
 * Tiny event bus so any page can open the assistant with a prompt and an entity,
 * without threading props or context through the app.
 */
export interface IAssistantEntity {
    type: 'user' | 'site' | 'access_point' | 'event' | 'visitor_pass' | 'schedule' | 'group' | 'unit' | 'integration' | 'device' | 'alert';
    id?: string;
    name?: string;
    email?: string;
    /** Row fields the model should start from (rendered into the prompt as data). */
    details?: Record<string, string | undefined>;
}

export interface IOpenAssistantOptions {
    /** Text to place in the composer (or send immediately when `send` is true). */
    prompt?: string;
    send?: boolean;
    entity?: IAssistantEntity;
}

const OPEN_EVENT = 'swiftlane:assistant:open';
const STATUS_EVENT = 'swiftlane:assistant:status';
const FORM_SAVED_EVENT = 'swiftlane:assistant:form-saved';

export interface IFormSaved {
    form: 'user' | 'visitor_pass' | 'door_unlock_schedule' | 'access_schedule' | 'site' | 'tenant';
    /** What was saved, in words: "Ann Lee", "Business hours". */
    summary?: string;
}

/** Forms call this after a successful create so an open conversation can continue on its own. */
export const notifyFormSaved = (saved: IFormSaved): void => {
    window.dispatchEvent(new CustomEvent<IFormSaved>(FORM_SAVED_EVENT, { detail: saved }));
};

export const onFormSaved = (handler: (saved: IFormSaved) => void): (() => void) => {
    const listener = (event: Event) => handler((event as CustomEvent<IFormSaved>).detail);
    window.addEventListener(FORM_SAVED_EVENT, listener);
    return () => window.removeEventListener(FORM_SAVED_EVENT, listener);
};

let enabledFlag = false;

export const openAssistant = (options: IOpenAssistantOptions = {}): void => {
    window.dispatchEvent(new CustomEvent<IOpenAssistantOptions>(OPEN_EVENT, { detail: options }));
};

export const onOpenAssistant = (handler: (options: IOpenAssistantOptions) => void): (() => void) => {
    const listener = (event: Event) => handler((event as CustomEvent<IOpenAssistantOptions>).detail || {});
    window.addEventListener(OPEN_EVENT, listener);
    return () => window.removeEventListener(OPEN_EVENT, listener);
};

/** The panel publishes whether the assistant is available; entry points hide otherwise. */
export const publishAssistantEnabled = (enabled: boolean): void => {
    enabledFlag = enabled;
    window.dispatchEvent(new CustomEvent<boolean>(STATUS_EVENT, { detail: enabled }));
};

export const isAssistantEnabled = (): boolean => enabledFlag;

export const onAssistantEnabledChange = (handler: (enabled: boolean) => void): (() => void) => {
    const listener = (event: Event) => handler((event as CustomEvent<boolean>).detail);
    window.addEventListener(STATUS_EVENT, listener);
    return () => window.removeEventListener(STATUS_EVENT, listener);
};

export const isMac = (): boolean => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

/**
 * Label for the assistant half of a split Add button. A bare sparkle icon was not
 * understood (feedback 2026-11-02); the label repeats the button's verb: "Add User"
 * -> "Add with AI", "Create Visitor Pass" -> "Create with AI", anything else -> "Create with AI".
 */
export const assistantActionLabel = (title: string): string => {
    const verb = (title || '').trim().split(/\s+/)[0] || '';
    return /^(add|create|new|invite|schedule)$/i.test(verb)
        ? `${verb[0].toUpperCase()}${verb.slice(1).toLowerCase()} with AI`
        : 'Create with AI';
};

/**
 * The dashboard session is the only source of truth for the workspace (2026-11-03): the
 * panel's own switcher left the bot acting in one account while the page, its forms and
 * its row context stayed in another. A conversation from another workspace is read-only
 * here; the admin starts a new one.
 */
export const conversationMatchesWorkspace = (conversationCompanyId?: string | null, dashboardCompanyId?: string | null): boolean =>
    !conversationCompanyId || !dashboardCompanyId || String(conversationCompanyId) === String(dashboardCompanyId);
